import React from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { colors, fonts, shadows } from "../theme";
import { compassFromDeg } from "../lib/weather";
import { locationLabel, systemLabel } from "../lib/kbLogic";
import { ASSUMPTIONS } from "../lib/citations";
import { useStudio } from "../store/useStudio";
import ScenarioCompare from "./ScenarioCompare";

const DownloadGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" /><path d="M7 11l5 5 5-5" /><path d="M4 20h16" />
  </svg>
);

const PencilGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

function fmt(n, d = 1) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toFixed(d);
}

const METRICS = [
  { key: "water_recovery", label: "Water Recovery", unit: "%", accent: colors.pillar.location, d: 0,
    scope: "Volumetric ratio of harvested water supply to crop irrigation (ET) demand. Rain + greywater are counted only when the matching Resource loop is active.",
    formula: "Recovery_water = (V_rain + V_greywater) ÷ V_irrigation_demand\nV_rain = precip × roofArea × runoff(0.80);  V_greywater = occupants × L/pp·day × 365 × 0.90" },
  { key: "total_yield", label: "Total Yield", unit: "kg", accent: colors.pillar.crops, d: 1,
    scope: "Total agricultural output of the selected BIA systems (per cycle).",
    formula: "Σ over systems:  (crop yield per m²·cycle × allocated m²)  +  Σ per-instance crop allocations (area × share × yield)" },
  { key: "energy_offset", label: "Energy Offset", unit: "%", accent: colors.pillar.systems, d: 0,
    scope: "Ratio of onsite generated/recovered energy to the BIA systems' operational (lighting + HVAC) demand.",
    formula: "Offset_energy = time-matched supply ÷ demand (day/night buckets)\nPV = daytime only; waste heat = building operating hours; grow-lights = photoperiod; HVAC/pumps = 24/7. Met = Σ min(supply, demand) per bucket" },
  { key: "carbon_offset", label: "Agriculture Carbon Savings", unit: "%", accent: colors.pillar.crops, d: 0,
    scope: "Net avoided emissions vs the 'Current Conventional System' — long-haul freight, refrigeration and the field/transit/distribution mass lost before produce reaches the plate — minus the design's own operational GHG. All baseline assumptions are editable below.",
    formula: "CarbonSavings = (CO₂_avoided − CO₂_footprint) ÷ CO₂_avoided\nGross_conventional = yield_edible ÷ (1 − loss);  CO₂_avoided = (Gross_conventional/1000) × food_miles_km × freightEF × (1 + reefer)" },
  { key: "structural_load_kg", label: "Structural Load", unit: "t", accent: colors.pillar.location, d: 1,
    toDisplay: (v) => (v || 0) / 1000,
    scope: "Total operating load the DRAFTED growing systems impose on the structure — sum of each drafted instance's structural load (kg/m²) × its area. Compare against your structural engineer's allowable roof/floor loading before committing.",
    formula: "Load_total = Σ_instances ( structural_load_kg/m² × area_m² )\nShown in tonnes (÷1000). Each instance's kg/m² is editable in its Draft card." },
  { key: "synergy", label: "Circularity", unit: "/100", accent: colors.pillar.resources, d: 0,
    scope: "The single circularity figure for this design — the weighted average of the four resource LOOPS (Water, Energy, Nutrient, Carbon) closed on-site. This is the exact same number as “Resources recovered” in the Circularity panel; adjust its loop weights (and presets / lock-to-equal) there.",
    formula: "Score = Σ(Wᵢ · loop_closureᵢ) ÷ ΣWᵢ  for i ∈ {water, energy, nutrient, carbon}\nloop_closure is each loop's own % (capped at 100%); weights are a user assumption (default equal 25%)." },
];

// Compact, honest provenance per KPI — the PRIMARY source(s), their tier, and data grade.
// "less is more": one line each. Full formula & citations live in the click-through info modal.
const PROVENANCE = {
  water_recovery: { source: "FAO-56 ET · ASCE runoff", tier: "Government / Standard", grade: "A" },
  total_yield: { source: "BIA Master DB v5.1 — crop yields", tier: "Database", grade: "B" },
  energy_offset: { source: "NREL PVWatts · CER solar", tier: "Government", grade: "A" },
  carbon_offset: { source: "GLEC v3.2 freight · FAO 2023 loss", tier: "Standard / Government", grade: "A" },
  structural_load_kg: { source: "System DB load (kg/m²) · ASCE/IBC", tier: "Database / Standard", grade: "B" },
  synergy: { source: "Derived composite · user loop weights", tier: "Derived", grade: "—" },
};
const GRADE_COLOR = { A: "#00843D", B: "#B8860B", C: "#C2410C", D: "#B42318" };

export default function KPIRibbon({ kpis, kb, expanded, onToggle, height, selections, weather, geo, scenarios = [], onDeleteScenario, onLoadScenario, onExportProject, canExport, onRenameScenario }) {
  const [compareIds, setCompareIds] = React.useState([]);
  const [showCompare, setShowCompare] = React.useState(false);
  const [infoKey, setInfoKey] = React.useState(null);
  const [hover, setHover] = React.useState(null); // { key, rect }
  const infoMetric = METRICS.find((m) => m.key === infoKey);
  const hoverProv = hover && PROVENANCE[hover.key];

  const toggleCompare = (id) =>
    setCompareIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : ids.length >= 4 ? ids : [...ids, id]
    );

  const selectedForCompare = scenarios.filter((s) => compareIds.includes(s.id));

  return (
    <motion.div
      style={styles.wrap}
      animate={{ height: expanded ? height : 138 }}
      transition={{ type: "spring", stiffness: 130, damping: 22 }}
      data-testid="kpi-ribbon"
    >
      <button style={styles.toggle} onClick={onToggle} data-testid="kpi-expand-button">
        <span style={styles.grip} />
        <span style={styles.toggleLabel}>{expanded ? "Collapse dashboard" : "Expand dashboard"}</span>
      </button>
      {onExportProject && (
        <button
          style={{ ...styles.exportProjectBtn, ...(canExport ? {} : styles.exportProjectBtnDisabled) }}
          disabled={!canExport}
          onClick={onExportProject}
          data-testid="export-project-btn"
          title={canExport ? "Save the whole project (state + geometry) to a .rcengine file" : "Load a building to enable export"}
        >
          <DownloadGlyph />
          Export Project
        </button>
      )}

      <div style={styles.metricsRow}>
        {METRICS.map((m, i) => {
          const cropGated = m.key === "total_yield" && kpis.total_area > 0 && !(kpis.total_yield > 0);
          const dispVal = m.toDisplay ? m.toDisplay(kpis[m.key]) : kpis[m.key];
          return (
            <button key={m.key} type="button" onClick={() => setInfoKey(m.key)}
              onMouseEnter={(e) => setHover({ key: m.key, rect: e.currentTarget.getBoundingClientRect() })}
              onMouseLeave={() => setHover(null)}
              style={{ ...styles.metric, ...styles.metricBtn, borderRight: i < METRICS.length - 1 ? `1px solid ${colors.border}` : "none" }} data-testid={`kpi-${m.key}`} title="View the exact formula & citations">
              <div style={styles.metricLabel}>
                <span style={{ ...styles.metricDot, background: m.accent }} />
                {m.label}
                <span style={styles.metricInfo} aria-hidden>ⓘ</span>
              </div>
              <div style={styles.metricValue}>
                {fmt(dispVal, m.d)}
                <span style={styles.metricUnit}>{m.unit}</span>
              </div>
              {cropGated && <div style={styles.metricGate} data-testid="kpi-yield-gate">Add a crop & set its share % to project yield</div>}
            </button>
          );
        })}
      </div>

      {expanded && (
        <div style={styles.expanded}>
          <div style={styles.cardsRow}>
            <Card title="RESOURCE FLOW LOG" grow={1.4}>
              <FlowLog selections={selections} weather={weather} geo={geo} kb={kb} />
            </Card>
            <Card title="YIELD PROJECTION" grow={1}>
              <BarChart accent={colors.pillar.crops} data={[
                { label: "Q1", v: kpis.total_yield * 0.6 },
                { label: "Q2", v: kpis.total_yield * 0.95 },
                { label: "Q3", v: kpis.total_yield * 1.1 },
                { label: "Q4", v: kpis.total_yield * 0.85 },
              ]} />
            </Card>
            <Card title="WATER BALANCE" grow={1}>
              <BarChart accent={colors.pillar.location} data={[
                { label: "DEMAND", v: 100 },
                { label: "SUPPLY", v: Math.min(140, kpis.water_recovery) },
                { label: "GAP", v: Math.max(0, 100 - kpis.water_recovery) },
              ]} />
            </Card>
            <Card title="CLIMATE" grow={1}>
              <ClimateMini weather={weather} />
            </Card>
          </div>
          <div style={styles.summaryGrid}>
            <SummaryTile label="WATER SAVINGS" value={fmt(kpis.balance?.water.supply_L, 0)} unit="L/yr" />
            <SummaryTile label="NET CO₂ SAVINGS" value={fmt((kpis.balance?.carbon.net_kg || 0) / 1000, 2)} unit="t CO₂e/yr" />
            <SummaryTile label="CIRCULAR LOOPS" value={String(selections.resources.length)} unit="active" />
            <SummaryTile label="SPECIES" value={String(selections.crops.length)} unit="crops" />
            <SummaryTile label="SYSTEMS" value={String(selections.systems.length)} unit="active" />
          </div>

          {(kpis.energy_balance_pc != null || kpis.carbon_offset_pc != null || kpis.water_offset_pc != null) ? (
            <div data-testid="per-capita-ribbon">
              <div style={styles.perCapitaHead}>PER-CAPITA ENVIRONMENTAL IMPACT · /person/yr</div>
              <div style={styles.summaryGrid}>
                <SummaryTile label="ENERGY BALANCE" value={fmt(kpis.energy_balance_pc, 0)} unit="kWh" />
                <SummaryTile label="CARBON SAVINGS" value={fmt(kpis.carbon_offset_pc, 0)} unit="kg CO₂e" />
                <SummaryTile label="POTABLE WATER OFFSET" value={fmt(kpis.water_offset_pc, 0)} unit="L" />
              </div>
            </div>
          ) : (
            <div style={styles.perCapitaHint} data-testid="per-capita-hint">
              Set the building typology and occupancy to view per-capita energy, carbon, and water impact.
            </div>
          )}

          <div style={styles.scenarioHead} data-testid="scenarios-section">
            <span style={styles.scenarioTitle}>SAVED SCENARIOS</span>
            <span style={styles.scenarioCount}>{scenarios.length} saved · compare cross-linked configurations</span>
            {scenarios.length >= 2 && (
              <button
                style={{ ...styles.compareBtn, ...(selectedForCompare.length < 2 ? styles.compareBtnDisabled : {}) }}
                disabled={selectedForCompare.length < 2}
                onClick={() => setShowCompare(true)}
                data-testid="compare-launch-btn"
              >
                Compare {selectedForCompare.length > 0 ? `(${selectedForCompare.length})` : ""}
              </button>
            )}
          </div>
          {scenarios.length === 0 ? (
            <div style={styles.scenarioEmpty} data-testid="scenarios-empty">
              No scenarios saved yet. Configure your loops and select <strong>Save</strong> to store a comparison card here.
            </div>
          ) : (
            <div style={styles.scenarioRow}>
              {scenarios.map((sc) => (
                <ScenarioCard
                  key={sc.id}
                  sc={sc}
                  onDelete={onDeleteScenario}
                  onLoad={onLoadScenario}
                  onRename={onRenameScenario}
                  selected={compareIds.includes(sc.id)}
                  onToggleCompare={() => toggleCompare(sc.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {showCompare && selectedForCompare.length >= 2 && (
        <ScenarioCompare
          scenarios={selectedForCompare}
          kb={kb}
          onClose={() => setShowCompare(false)}
          onLoad={(sc) => {
            setShowCompare(false);
            onLoadScenario?.(sc);
          }}
        />
      )}
      {hoverProv && hover.rect && createPortal(
        <div data-testid="kpi-provenance-tip"
          style={{
            position: "fixed",
            left: Math.min(hover.rect.left, window.innerWidth - 272),
            bottom: window.innerHeight - hover.rect.top + 8,
            width: 256, zIndex: 11000, background: "#1c1c1e", color: "#fff",
            borderRadius: 10, padding: "10px 12px", boxShadow: "0 10px 34px rgba(0,0,0,0.34)",
            fontFamily: fonts.sans, pointerEvents: "none",
          }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.7, color: "#8e8e93", marginBottom: 4 }}>PROVENANCE</div>
          <div style={{ fontSize: 12.5, fontWeight: 650, lineHeight: 1.35 }}>{hoverProv.source}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            <span style={{ fontSize: 10, color: "#c7c7cc" }}>{hoverProv.tier}</span>
            <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 999,
              color: "#fff", background: GRADE_COLOR[hoverProv.grade] || "#636366" }}>Grade {hoverProv.grade}</span>
          </div>
          <div style={{ fontSize: 9.5, color: "#8e8e93", marginTop: 7 }}>Click for formula &amp; full citations</div>
        </div>,
        document.body
      )}
      {infoMetric && createPortal(
        <div style={styles.infoScrim} onClick={() => setInfoKey(null)} data-testid="kpi-info-modal">
          <div style={styles.infoCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.infoHead}>
              <span style={{ ...styles.metricDot, background: infoMetric.accent, width: 10, height: 10 }} />
              <span style={styles.infoTitle}>{infoMetric.label}</span>
              <button style={styles.infoClose} onClick={() => setInfoKey(null)} data-testid="kpi-info-close">×</button>
            </div>
            <div style={styles.infoValue}>{fmt(infoMetric.toDisplay ? infoMetric.toDisplay(kpis[infoMetric.key]) : kpis[infoMetric.key], infoMetric.d)}<span style={styles.infoValueUnit}>{infoMetric.unit}</span></div>
            <div style={styles.infoSectionLabel}>WHAT IT MEASURES</div>
            <div style={styles.infoScope}>{infoMetric.scope}</div>
            <div style={styles.infoSectionLabel}>EXACT FORMULA</div>
            <div style={styles.infoFormula} data-testid="kpi-info-formula">{infoMetric.formula}</div>
            {infoMetric.key === "structural_load_kg" && <StructuralLoadBreakdown rows={kpis.structural_load_rows} totalKg={kpis.structural_load_kg} />}
            <BalanceBreakdown metricKey={infoMetric.key} balance={kpis.balance} />
            {infoMetric.key === "synergy" && <CircWeightsNote />}
            {infoMetric.key === "carbon_offset" && <CarbonBaselineEditor />}
            <AssumptionsBlock metricKey={infoMetric.key} />
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
}

function BalanceBreakdown({ metricKey, balance }) {
  if (!balance) return null;
  let rows = null;
  if (metricKey === "water_recovery") {
    const w = balance.water;
    rows = [["Irrigation demand (ET)", `${w.demand_L.toLocaleString()} L/yr`], ["Rainwater harvest", `${w.rain_L.toLocaleString()} L/yr`], ["Greywater reuse", `${w.greywater_L.toLocaleString()} L/yr`], ["Total supply", `${w.supply_L.toLocaleString()} L/yr`]];
    if (w.surplus_L > 0) rows.push(["Net-positive surplus", `+${w.surplus_L.toLocaleString()} L/yr`, true]);
  } else if (metricKey === "energy_offset") {
    const e = balance.energy;
    rows = [["Operational demand", `${e.demand_kWh.toLocaleString()} kWh/yr`], ["Solar PV generation", `${e.solar_kWh.toLocaleString()} kWh/yr`]];
    if ((e.lighting_derived_kWh || 0) > 0) rows.push(["…incl. LED lighting (DLI-derived)", `${e.lighting_derived_kWh.toLocaleString()} kWh/yr`]);
    if ((e.solar_thermal_kWh || 0) > 0) rows.push(["Solar thermal (heat)", `${e.solar_thermal_kWh.toLocaleString()} kWh/yr`]);
    rows.push(["Recovered waste heat", `${e.wasteheat_kWh.toLocaleString()} kWh/yr`]);
    if ((e.biogas_kWh || 0) > 0) rows.push(["Biogas generation", `${e.biogas_kWh.toLocaleString()} kWh/yr`]);
    if ((e.glazing_solar_kWh || 0) > 0) rows.push(["Passive solar gain (glazing)", `${e.glazing_solar_kWh.toLocaleString()} kWh/yr`]);
    if ((balance.spatial?.glazing_split || []).length > 0) rows.push(["…façade split (NREL)", balance.spatial.glazing_split.map((s) => `${s.label} ${s.area_m2}m²×${s.factor.toFixed(2)}`).join(" · ")]);
    if (balance.spatial?.interior_dli != null) rows.push(["Daylight admitted (interior DLI)", `${balance.spatial.interior_dli} mol/m²·d · VLT ${Math.round((balance.spatial.glazing_vlt || 0) * 100)}%`]);
    rows.push(["Total supply", `${e.supply_kWh.toLocaleString()} kWh/yr`]);
    if (e.surplus_kWh > 0) rows.push(["Net-positive surplus", `+${e.surplus_kWh.toLocaleString()} kWh/yr`, true]);
  } else if (metricKey === "carbon_offset") {
    const c = balance.carbon;
    rows = [
      ["On-site edible yield", `${(c.edible_yield_kg || 0).toLocaleString()} kg/yr`],
      ["Conventional gross (incl. spoilage)", `${(c.gross_conventional_kg || 0).toLocaleString()} kg/yr`],
      ["…of which lost in field/transit", `${(c.wasted_kg || 0).toLocaleString()} kg/yr`],
      ...(c.transport_factor_kg_per_kg ? [["Transport basis", `${c.transport_factor_kg_per_kg} kg CO₂e/kg · ${c.transport_basis || "cited"}`]] : []),
      ["Freight avoided", `${(c.avoided_kg || 0).toLocaleString()} kg CO₂e/yr`],
      ["Operational footprint", `${(c.footprint_kg || 0).toLocaleString()} kg CO₂e/yr`],
      ...(c.grid_factor_kg_per_kwh != null ? [["Grid intensity", `${c.grid_factor_kg_per_kwh} kg CO₂e/kWh · ${c.grid_province || "—"}${c.grid_auto ? " (auto)" : ""}`]] : []),
      ["Net savings", `${(c.net_kg || 0).toLocaleString()} kg CO₂e/yr`, (c.net_kg || 0) > 0],
    ];
  } else if (metricKey === "spatial") {
    const sp = balance.spatial;
    rows = [["Productive area", `${sp.area_m2.toLocaleString()} m²`]];
    if (sp.interior_dli != null) {
      rows.push(["Glazed daylight area", `${sp.glazing_area_m2.toLocaleString()} m² · VLT ${Math.round(sp.glazing_vlt * 100)}%`]);
      rows.push(["Interior DLI (behind glass)", `${sp.interior_dli} mol/m²·d`]);
    }
  }
  if (!rows) return null;
  return (
    <>
      <div style={styles.infoSectionLabel}>SITE-CALIBRATED BALANCE</div>
      <div style={styles.balanceBox} data-testid="kpi-balance-breakdown">
        {rows.map(([k, v, hl]) => (
          <div key={k} style={styles.balanceRow}><span>{k}</span><span style={{ ...styles.balanceVal, ...(hl ? { color: "#00B26A" } : {}) }}>{v}</span></div>
        ))}
      </div>
    </>
  );
}

// P2 — per-instance structural-load table shown in the KPI info modal (drafted blocks only).
function StructuralLoadBreakdown({ rows, totalKg }) {
  if (!rows?.length) {
    return (
      <>
        <div style={styles.infoSectionLabel}>PER-INSTANCE LOAD</div>
        <div style={styles.balanceBox} data-testid="kpi-structural-breakdown">
          <div style={styles.balanceRow}><span>No systems drafted yet</span><span style={styles.balanceVal}>—</span></div>
        </div>
      </>
    );
  }
  return (
    <>
      <div style={styles.infoSectionLabel}>PER-INSTANCE LOAD · DRAFTED SYSTEMS</div>
      <div style={styles.balanceBox} data-testid="kpi-structural-breakdown">
        {rows.map((r) => (
          <div key={r.id} style={styles.balanceRow}>
            <span>{r.label} · {Math.round(r.area).toLocaleString()} m² × {Math.round(r.loadKgM2).toLocaleString()} kg/m²</span>
            <span style={styles.balanceVal}>{(r.kg / 1000).toFixed(1)} t</span>
          </div>
        ))}
        <div style={styles.balanceRow}><span><strong>Total structural load</strong></span><span style={{ ...styles.balanceVal, color: colors.textPrimary }}><strong>{(totalKg / 1000).toFixed(1)} t</strong></span></div>
      </div>
    </>
  );
}


function CircWeightsNote() {
  const circWeights = useStudio((s) => s.circWeights);
  const circLockEqual = useStudio((s) => s.circLockEqual);
  const pct = (x) => Math.round(((circLockEqual ? 0.25 : (circWeights[x] || 0))) * 100);
  return (
    <>
      <div style={styles.infoSectionLabel}>LOOP WEIGHTS {circLockEqual ? "· LOCKED TO EQUAL" : "· USER ASSUMPTION"}</div>
      <div style={styles.weightsBox} data-testid="circ-weights-note">
        {[["water", "Water"], ["energy", "Energy"], ["nutrient", "Nutrient"], ["carbon", "Carbon"]].map(([k, label]) => (
          <div key={k} style={styles.weightRow}>
            <span style={styles.weightLabel}>{label}</span>
            <span style={styles.weightPct}>{pct(k)}%</span>
          </div>
        ))}
        <div style={{ fontSize: 10.5, color: colors.textTertiary, marginTop: 8, lineHeight: 1.5 }}>
          Edit these weights (plus presets &amp; lock-to-equal) in the <strong>Circularity</strong> panel — “Resources recovered → adjust weights”. The score here always matches that panel.
        </div>
      </div>
    </>
  );
}

function AssumptionsBlock({ metricKey }) {
  const map = { water_recovery: "water", energy_offset: "energy", carbon_offset: "carbon", spatial: "spatial", synergy: null };
  const a = ASSUMPTIONS[map[metricKey]];
  if (!a) return null;
  return (
    <>
      <div style={styles.infoSectionLabel}>ASSUMPTIONS & CITATIONS</div>
      <div style={styles.citeBox} data-testid="kpi-citations">
        <div style={styles.citeDetail}>{a.detail}</div>
        {a.constants.map(([n, v, src]) => (
          <div key={n} style={styles.citeConst}><b>{n}</b> = {v} <span style={styles.citeSrc}>· {src}</span></div>
        ))}
        <div style={styles.citeRefsHead}>References</div>
        {a.refs.map((r) => (
          <a key={r.url} href={r.url} target="_blank" rel="noreferrer" style={styles.citeLink}>{r.label} ↗</a>
        ))}
      </div>
    </>
  );
}

function CarbonBaselineEditor() {
  const cb = useStudio((s) => s.carbonBaseline);
  const setCB = useStudio((s) => s.setCarbonBaseline);
  const cities = useStudio((s) => s.kb?.loc_db?.cities) || [];
  const citeActive = Number(cb?.transportFactorKgPerKg) > 0;
  const fields = [
    { key: "foodMilesKm", label: "Food-miles distance", unit: "km", step: 50, min: 0, max: 20000, scale: 1, generic: true },
    { key: "freightEF", label: "Road-freight factor", unit: "kg CO₂e/t·km", step: 0.001, min: 0, max: 1, scale: 1, generic: true },
    { key: "reeferUplift", label: "Refrigeration uplift", unit: "%", step: 1, min: 0, max: 100, scale: 100, generic: true },
    { key: "lossFraction", label: "Supply-chain loss", unit: "%", step: 1, min: 0, max: 90, scale: 100 },
    { key: "nutrientLossPct", label: "Nutrient-density loss", unit: "%", step: 1, min: 0, max: 90, scale: 100 },
  ];
  const pickTransport = (val) => {
    if (!val) {
      // Explicit "Generic" — sentinel transportMode blocks geo re-auto-adoption.
      setCB("transportFactorKgPerKg", null); setCB("transportBasis", null);
      setCB("transportCity", null); setCB("transportMode", "generic"); setCB("transportAuto", false); return;
    }
    const [cityName, mode] = val.split("|");
    const c = cities.find((x) => x.city === cityName);
    if (!c) return;
    const f = mode === "provincial" ? c.transport_provincial_kgco2e_per_kg : c.transport_import_kgco2e_per_kg;
    const dist = mode === "provincial" ? c.dist_provincial_km : c.dist_import_km;
    const label = mode === "provincial"
      ? `Provincial greenhouse · ${c.city} · ${dist ?? "—"} km`
      : `Imported (AZ/CA) · ${c.city} · ${dist ?? "—"} km`;
    setCB("transportFactorKgPerKg", f);
    setCB("transportBasis", `${label} — ${f} kg CO₂e/kg (cited)`);
    setCB("transportCity", c.city); setCB("transportMode", mode); setCB("transportAuto", false);
  };
  const selectValue = (citeActive && cb?.transportCity && cb?.transportMode)
    ? `${cb.transportCity}|${cb.transportMode}` : "";
  return (
    <>
      <div style={styles.infoSectionLabel}>CURRENT CONVENTIONAL SYSTEM · EDITABLE BASELINE</div>
      <div style={styles.weightsBox} data-testid="carbon-baseline-editor">
        {cities.length > 0 && (
          <div style={{ ...styles.cbRow, alignItems: "flex-start", flexWrap: "wrap" }}>
            <span style={styles.cbLabel}>Transport-carbon source</span>
            <select
              value={selectValue}
              onChange={(e) => pickTransport(e.target.value)}
              style={{ ...styles.cbInput, width: 210 }}
              data-testid="carbon-transport-source"
            >
              <option value="">Generic food-miles (editable)</option>
              {cities.filter((c) => c.transport_import_kgco2e_per_kg != null).map((c) => (
                <option key={`${c.city}-i`} value={`${c.city}|import`}>{c.city} — imported ({c.transport_import_kgco2e_per_kg} kg/kg)</option>
              ))}
              {cities.filter((c) => c.transport_provincial_kgco2e_per_kg != null).map((c) => (
                <option key={`${c.city}-p`} value={`${c.city}|provincial`}>{c.city} — local greenhouse ({c.transport_provincial_kgco2e_per_kg} kg/kg)</option>
              ))}
            </select>
          </div>
        )}
        {citeActive && (
          <div style={{ ...styles.cbNote, color: colors.pillar?.location || undefined }} data-testid="carbon-transport-cited-note">
            Using cited transport factor: {cb.transportBasis}. The generic food-miles fields below are overridden.
          </div>
        )}
        {fields.map((f) => {
          const raw = cb?.[f.key] ?? 0;
          const shown = f.scale === 100 ? Math.round(raw * 100) : raw;
          const disabled = f.generic && citeActive;
          return (
            <div key={f.key} style={{ ...styles.cbRow, opacity: disabled ? 0.45 : 1 }}>
              <span style={styles.cbLabel}>{f.label}</span>
              <input
                type="number" step={f.step} min={f.min} max={f.max} value={shown} disabled={disabled}
                onChange={(e) => {
                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                  setCB(f.key, f.scale === 100 ? Math.max(0, Math.min(0.9, (v || 0) / 100)) : Math.max(0, v || 0));
                }}
                style={styles.cbInput}
                data-testid={`carbon-baseline-${f.key}`}
              />
              <span style={styles.cbUnit}>{f.unit}</span>
            </div>
          );
        })}
        <div style={styles.cbNote}>Nutrient-density loss is shown for transparency and is not folded into the CO₂ math.</div>
      </div>
    </>
  );
}

function Card({ title, children, grow }) {
  return (
    <div style={{ ...styles.card, flex: grow }}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

function BarChart({ data, accent }) {
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", height: 116, gap: 12 }}>
      {data.map((d) => (
        <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ width: "66%", borderRadius: "6px 6px 0 0", background: `linear-gradient(180deg, ${accent}, ${accent}99)`, height: Math.max(4, (d.v / max) * 88), transition: "height 0.5s cubic-bezier(0.16,1,0.3,1)" }} />
          <div style={styles.chartLabel}>{d.label}</div>
          <div style={styles.chartValue}>{d.v.toFixed(1)}</div>
        </div>
      ))}
    </div>
  );
}

function ClimateMini({ weather }) {
  if (!weather) return <div style={styles.miniEmpty}>No climate yet.</div>;
  const rows = [
    ["DLI", `${weather.dli} mol/m²·d`],
    ["GHI", `${weather.ghi} kWh/m²·d`],
    ["Wind", `${weather.windSpeed} km/h ${compassFromDeg(weather.windDir)}`],
    ["Precip", `${weather.precip} mm/yr`],
    ["GDD", `${weather.gdd}`],
    ["Frost-free", `${weather.frostFree} d`],
  ];
  return (
    <div>
      {rows.map(([k, v]) => (
        <div key={k} style={styles.logRow}>
          <span style={styles.logKey}>{k}</span>
          <span style={styles.logVal}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function FlowLog({ selections, weather, geo, kb }) {
  const rows = [
    ["SITE", geo?.name || "—"],
    ["ZONES", selections.locations.length ? selections.locations.map((l) => locationLabel(kb, l)).join(", ") : "—"],
    ["SYSTEMS", selections.systems.length ? selections.systems.map((s) => systemLabel(kb, s)).join(", ") : "—"],
    ["CROPS", selections.crops.length ? `${selections.crops.length} species` : "—"],
    ["RESOURCES", selections.resources.length ? `${selections.resources.length} loops` : "—"],
    ["CLIMATE SRC", weather ? (weather.source === "epw" ? ".EPW FILE" : weather.source === "estimate" ? "ESTIMATE" : "OPEN-METEO") : "—"],
  ];
  return (
    <div>
      {rows.map(([k, v]) => (
        <div key={k} style={styles.logRow}>
          <span style={styles.logKey}>{k}</span>
          <span style={styles.logVal}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryTile({ label, value, unit }) {
  return (
    <div style={styles.summaryTile}>
      <div style={styles.summaryLabel}>{label}</div>
      <div style={styles.summaryValue}>
        {value}
        <span style={styles.summaryUnit}>{unit}</span>
      </div>
    </div>
  );
}

function ScenarioCard({ sc, onDelete, onLoad, selected, onToggleCompare, onRename }) {
  const k = sc.kpis || {};
  const sel = sc.selections || {};
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(sc.name || "");
  const commitRename = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== sc.name) onRename?.(sc.id, next);
    else setDraft(sc.name || "");
  };
  const metrics = [
    ["Yield", fmt(k.total_yield, 1), "kg", colors.pillar.crops],
    ["Water", fmt(k.water_recovery, 0), "%", colors.pillar.location],
    ["Energy", fmt(k.energy_offset, 0), "%", colors.pillar.systems],
    ["Circularity", fmt(k.system_health, 0), "", colors.pillar.resources],
  ];
  return (
    <div style={{ ...styles.scenarioCard, ...(selected ? styles.scenarioCardSelected : {}) }} data-testid={`scenario-card-${sc.id}`}>
      <div style={styles.scenarioCardHead}>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setDraft(sc.name || ""); setEditing(false); } }}
            style={styles.scenarioNameInput}
            data-testid={`scenario-rename-input-${sc.id}`}
          />
        ) : (
          <span
            style={styles.scenarioName}
            title="Click to rename"
            onClick={() => onRename && setEditing(true)}
            data-testid={`scenario-name-${sc.id}`}
          >
            {sc.name}
          </span>
        )}
        {!editing && onRename && (
          <button style={styles.scenarioRename} data-testid={`scenario-rename-${sc.id}`} onClick={() => setEditing(true)} title="Rename scenario">
            <PencilGlyph />
          </button>
        )}
        {onDelete && (
          <button style={styles.scenarioDelete} data-testid={`scenario-delete-${sc.id}`} onClick={() => onDelete(sc.id)}>×</button>
        )}
      </div>
      <div style={styles.scenarioMeta}>
        {sc.onboarding?.location || "—"} · {(sel.systems || []).length} sys · {(sel.crops || []).length} crops · {fmt(k.total_area, 0)} m²
      </div>
      <div style={styles.scenarioMetrics}>
        {metrics.map(([label, val, unit, accent]) => (
          <div key={label} style={styles.scenarioMetric}>
            <div style={{ ...styles.scenarioMetricDot, background: accent }} />
            <div>
              <div style={styles.scenarioMetricVal}>{val}<span style={styles.scenarioMetricUnit}>{unit}</span></div>
              <div style={styles.scenarioMetricLabel}>{label}</div>
            </div>
          </div>
        ))}
      </div>
      {onToggleCompare && (
        <button
          style={{ ...styles.compareToggle, ...(selected ? styles.compareToggleOn : {}) }}
          onClick={onToggleCompare}
          data-testid={`scenario-compare-toggle-${sc.id}`}
          aria-pressed={selected}
        >
          <span style={{ ...styles.compareCheck, ...(selected ? styles.compareCheckOn : {}) }}>{selected ? "✓" : ""}</span>
          {selected ? "Selected to compare" : "Add to compare"}
        </button>
      )}
      {onLoad && (
        <button style={styles.scenarioLoad} data-testid={`scenario-load-${sc.id}`} onClick={() => onLoad(sc)}>
          Load into studio
        </button>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    width: "100%",
    flexShrink: 0,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    background: colors.glassStrong,
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    borderTop: `1px solid ${colors.glassBorder}`,
    boxShadow: "0 -8px 32px rgba(0,0,0,0.05)",
    overflow: "hidden",
    zIndex: 12,
  },
  toggle: { height: 28, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, border: "none", background: "transparent", cursor: "pointer", borderBottom: `1px solid ${colors.border}` },
  grip: { width: 40, height: 4, borderRadius: 999, background: colors.borderStrong },
  toggleLabel: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.5, color: colors.textTertiary, textTransform: "uppercase" },
  exportProjectBtn: { position: "absolute", top: 8, right: 16, zIndex: 5, display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 600, letterSpacing: 0.2, cursor: "pointer", boxShadow: shadows.soft },
  exportProjectBtnDisabled: { background: "rgba(0,0,0,0.08)", color: colors.textTertiary, cursor: "not-allowed", boxShadow: "none" },
  metricsRow: { display: "flex", height: 110, flexShrink: 0, padding: "0 12px", alignItems: "center" },
  metric: { flex: 1, padding: "0 20px", minWidth: 0 },
  metricBtn: { border: "none", background: "transparent", textAlign: "left", cursor: "pointer", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" },
  metricInfo: { marginLeft: 6, fontSize: 11, color: colors.textTertiary, opacity: 0.7 },
  metricLabel: { display: "flex", alignItems: "center", gap: 7, fontFamily: fonts.sans, fontSize: 12, fontWeight: 500, color: colors.textSecondary, marginBottom: 8 },
  metricDot: { width: 8, height: 8, borderRadius: 999 },
  metricValue: { fontFamily: fonts.sans, fontSize: 44, fontWeight: 700, letterSpacing: -1.5, color: colors.textPrimary, lineHeight: 1, display: "flex", alignItems: "baseline" },
  metricUnit: { fontFamily: fonts.sans, fontSize: 13, fontWeight: 500, color: colors.textTertiary, marginLeft: 6 },
  metricGate: { marginTop: 6, fontFamily: fonts.sans, fontSize: 10.5, fontWeight: 500, color: colors.warn },
  expanded: { flex: 1, padding: "16px 20px 22px", overflowY: "auto" },
  cardsRow: { display: "flex", gap: 14 },
  card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 16, boxShadow: shadows.soft },
  cardTitle: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.5, color: colors.textTertiary },
  chartLabel: { marginTop: 8, fontFamily: fonts.mono, fontSize: 10, color: colors.textSecondary },
  chartValue: { marginTop: 2, fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, color: colors.textPrimary },
  miniEmpty: { fontFamily: fonts.sans, fontSize: 12, color: colors.textTertiary },
  logRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${colors.border}` },
  logKey: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, color: colors.textTertiary },
  logVal: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 500, color: colors.textPrimary, maxWidth: "62%", textAlign: "right" },
  summaryGrid: { display: "flex", gap: 14, marginTop: 14 },
  perCapitaHead: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.4, color: colors.textSecondary, fontWeight: 700, marginTop: 22, marginBottom: 2 },
  perCapitaHint: { marginTop: 18, padding: "14px 16px", borderRadius: 14, border: `1px dashed ${colors.borderStrong}`, fontFamily: fonts.sans, fontSize: 12.5, color: colors.textSecondary },
  summaryTile: { flex: 1, padding: 16, borderRadius: 16, background: colors.surface, border: `1px solid ${colors.border}`, boxShadow: shadows.soft },
  summaryLabel: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, color: colors.textTertiary },
  summaryValue: { fontFamily: fonts.sans, fontSize: 32, fontWeight: 700, letterSpacing: -1, color: colors.textPrimary, marginTop: 8, display: "flex", alignItems: "baseline" },
  summaryUnit: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 500, color: colors.textTertiary, marginLeft: 6 },
  scenarioHead: { display: "flex", alignItems: "baseline", gap: 12, marginTop: 22, marginBottom: 12 },
  scenarioTitle: { fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: colors.textSecondary, fontWeight: 700 },
  scenarioCount: { fontFamily: fonts.sans, fontSize: 11.5, color: colors.textTertiary },
  compareBtn: { marginLeft: "auto", padding: "7px 16px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  compareBtnDisabled: { background: "rgba(0,0,0,0.08)", color: colors.textTertiary, cursor: "not-allowed" },
  scenarioCardSelected: { border: `1.5px solid ${colors.pillar.location}`, boxShadow: `0 0 0 3px ${colors.pillar.location}22, ${shadows.soft}` },
  compareToggle: { width: "100%", marginTop: 10, padding: "7px 10px", borderRadius: 10, border: `1px solid ${colors.border}`, background: "transparent", color: colors.textSecondary, fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 },
  compareToggleOn: { border: `1px solid ${colors.pillar.location}`, color: colors.pillar.location, background: `${colors.pillar.location}0d` },
  compareCheck: { width: 15, height: 15, borderRadius: 4, border: `1px solid ${colors.borderStrong}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, lineHeight: 1 },
  compareCheckOn: { border: "none", background: colors.pillar.location, color: "#fff" },
  scenarioEmpty: { padding: "16px 18px", borderRadius: 14, border: `1px dashed ${colors.borderStrong}`, fontFamily: fonts.sans, fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.5 },
  scenarioRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  scenarioCard: { width: 230, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 14, boxShadow: shadows.soft },
  scenarioCardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  scenarioName: { fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, color: colors.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0, cursor: "text" },
  scenarioNameInput: { flex: 1, minWidth: 0, fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, color: colors.textPrimary, border: `1px solid ${colors.brand}`, borderRadius: 7, padding: "3px 7px", outline: "none", background: "#fff" },
  scenarioRename: { width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textTertiary, cursor: "pointer", flexShrink: 0 },
  scenarioDelete: { width: 22, height: 22, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textTertiary, fontSize: 15, lineHeight: 1, cursor: "pointer", flexShrink: 0 },
  scenarioMeta: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.3, color: colors.textTertiary, marginTop: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  scenarioMetrics: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 },
  scenarioMetric: { display: "flex", alignItems: "center", gap: 7 },
  scenarioMetricDot: { width: 7, height: 7, borderRadius: 999, flexShrink: 0 },
  scenarioMetricVal: { fontFamily: fonts.sans, fontSize: 16, fontWeight: 700, letterSpacing: -0.5, color: colors.textPrimary, lineHeight: 1, display: "flex", alignItems: "baseline" },
  scenarioMetricUnit: { fontFamily: fonts.sans, fontSize: 9, fontWeight: 500, color: colors.textTertiary, marginLeft: 2 },
  scenarioMetricLabel: { fontFamily: fonts.mono, fontSize: 8, letterSpacing: 0.5, color: colors.textTertiary, marginTop: 2 },
  scenarioLoad: { width: "100%", marginTop: 12, padding: "8px 12px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  infoScrim: { position: "fixed", inset: 0, background: "rgba(16,18,22,0.44)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90, padding: 20 },
  infoCard: { width: 460, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto", background: colors.surface, borderRadius: 20, border: `1px solid ${colors.border}`, boxShadow: shadows.float, padding: "0 24px 26px" },
  infoHead: { position: "sticky", top: 0, zIndex: 2, background: colors.surface, display: "flex", alignItems: "center", gap: 9, paddingTop: 22, paddingBottom: 10, marginBottom: 2 },
  infoTitle: { fontFamily: fonts.sans, fontSize: 18, fontWeight: 700, letterSpacing: -0.4, color: colors.textPrimary },
  infoClose: { marginLeft: "auto", width: 28, height: 28, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, fontSize: 18, lineHeight: 1, cursor: "pointer" },
  infoValue: { fontFamily: fonts.sans, fontSize: 40, fontWeight: 700, letterSpacing: -1.5, color: colors.textPrimary, marginTop: 10, display: "flex", alignItems: "baseline" },
  infoValueUnit: { fontFamily: fonts.sans, fontSize: 14, fontWeight: 500, color: colors.textTertiary, marginLeft: 6 },
  infoSectionLabel: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.5, color: colors.textTertiary, marginTop: 20, marginBottom: 7 },
  infoScope: { fontFamily: fonts.sans, fontSize: 13.5, lineHeight: 1.5, color: colors.textSecondary },
  infoFormula: { fontFamily: fonts.mono, fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-line", color: colors.textPrimary, background: colors.canvas, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "12px 14px" },
  balanceBox: { background: colors.canvas, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "10px 14px" },
  balanceRow: { display: "flex", justifyContent: "space-between", fontFamily: fonts.sans, fontSize: 12.5, color: colors.textSecondary, padding: "3px 0" },
  balanceVal: { fontFamily: fonts.mono, fontWeight: 700, color: colors.textPrimary },
  weightsBox: { background: colors.canvas, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "10px 14px" },
  weightRow: { display: "flex", alignItems: "center", gap: 10, padding: "4px 0" },
  cbRow: { display: "flex", alignItems: "center", gap: 10, padding: "4px 0" },
  cbLabel: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, color: colors.textSecondary, flex: 1 },
  cbInput: { width: 78, padding: "5px 8px", borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, color: colors.textPrimary, textAlign: "right" },
  cbUnit: { fontFamily: fonts.mono, fontSize: 9.5, color: colors.textTertiary, width: 74 },
  cbNote: { fontFamily: fonts.sans, fontSize: 10.5, color: colors.textTertiary, marginTop: 8, lineHeight: 1.4, fontStyle: "italic" },
  weightLabel: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, color: colors.textSecondary, width: 58 },
  weightPct: { fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, color: colors.textPrimary, width: 40, textAlign: "right" },
  citeBox: { background: colors.canvas, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "10px 14px" },
  citeDetail: { fontFamily: fonts.sans, fontSize: 12.5, lineHeight: 1.55, color: colors.textSecondary, marginBottom: 8 },
  citeConst: { fontFamily: fonts.sans, fontSize: 12, color: colors.textPrimary, padding: "2px 0" },
  citeSrc: { color: colors.textTertiary, fontSize: 11 },
  citeRefsHead: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 1, color: colors.textTertiary, margin: "10px 0 4px" },
  citeLink: { display: "block", fontFamily: fonts.sans, fontSize: 12, color: colors.pillar.location, textDecoration: "none", padding: "3px 0", lineHeight: 1.4 },
};
